// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import { combineReducers } from 'redux';
import { forEach, get, isArray, lowerCase } from 'lodash';

import * as Constants from '../../constants/metrics';

const isMetricsAction = actionType =>
  [
    Constants.METRICS_DATA_REQUEST,
    Constants.METRICS_DATA_SUCCESS,
    Constants.METRICS_DATA_ERROR,
  ].includes(actionType);

const isCustomChartMetricsAction = actionType =>
  [
    Constants.METRICS_CUSTOM_CHART_DATA_REQUEST,
    Constants.METRICS_CUSTOM_CHART_DATA_SUCCESS,
    Constants.METRICS_CUSTOM_CHART_DATA_ERROR,
  ].includes(actionType);

const parseAPIListResponse = (action) => {
  const apiMap = {};
  const apiList = (action && action.payload &&
    isArray(action.payload) && action.payload) || [];
  forEach(apiList, apiItem => {
    apiMap[apiItem.Uuid] = apiItem.Name;
  });
  return apiMap;
};

const parseApiKeyListResponse = (action) => {
  const apiKeyMap = {};
  const apiKeyList = (action && action.payload && action.payload.results &&
   isArray(action.payload.results) && action.payload.results) || [];
  forEach(apiKeyList, apiKeyItem => {
    apiKeyMap[apiKeyItem.uuid] = apiKeyItem.display;
  });
  return apiKeyMap;
};

const parseEntityListResponse = (action) => {
  const appMap = {};
  const appList = (action && action.payload && action.payload.results &&
    isArray(action.payload.results) && action.payload.results) || [];
  forEach(appList, appItem => {
    appMap[appItem.uuid] = appItem.name;
  });
  return appMap;
};

const getApiKeyDetailsFromResponse = (action) => {
  const apiKeyAPPMap = {};
  const apiKeyList = (action && action.payload && action.payload.results &&
    isArray(action.payload.results) && action.payload.results) || [];
  forEach(apiKeyList, item => {
    apiKeyAPPMap[item.uuid] = {
      name: item.apiKeyName,
      secondaryName: item.applicationName,
      appName: item.applicationName,
      orgName: item.organizationName,
    };
  });
  return apiKeyAPPMap;
};

const parseGatewayListResponse = action => {
  const gatewayMap = {};
  const gatewayList =
    (action && isArray(action.payload) && action.payload) || [];
  gatewayList
    .filter(gateway => gateway.enrollmentStatus === 'ACTIVE')
    .forEach(gateway => {
      gatewayMap[gateway.uuid] = gateway.name;
    });
  return gatewayMap;
};

export const data = (state = {}, action) => {
  if (!isMetricsAction(action.type)) {
    return state;
  }
  const chartID = action.chartID;
  let chartData = { default: {}, [action.metricType]: {} };
  if (action.metricType) {
    chartData = {
      [action.metricType]: {
        loading: false,
        data: {},
        query: {},
      },
    };
  } else {
    chartData = {
      default: {
        loading: false,
        data: {},
        query: {},
      },
    };
  }
  switch (action.type) {
    case Constants.METRICS_DATA_REQUEST:
      if (action.metricType) {
        chartData = {
          ...state[chartID],
          [action.metricType]: {
            loading: true,
            data: {},
            query: {},
          },
        };
      } else {
        chartData = {
          default: {
            loading: true,
            data: {},
            query: {},
          },
        };
      }
      break;
    case Constants.METRICS_DATA_SUCCESS:
      if (action.metricType) {
        chartData = {
          ...state[chartID],
          [action.metricType]: {
            loading: false,
            data: (action.payload && action.payload.data) || {},
            query: (action.payload && action.payload.query) || {},
          },
        };
      } else {
        chartData = {
          default: {
            loading: false,
            timeStamp: new Date().getTime(),
            data: (action.payload && action.payload.data) || {},
            query: (action.payload && action.payload.query) || {},
          },
        };
      }
      break;
    case Constants.METRICS_DATA_ERROR:
      if (action.metricType) {
        chartData = {
          ...state[chartID],
          [action.metricType]: {
            loading: false,
            data: {},
            query: (action.payload && action.payload.query) || {},
          },
        };
      } else {
        chartData = {
          default: {
            loading: false,
            timeStamp: new Date().getTime(),
            data: {},
            query: (action.payload && action.payload.query) || {},
          },
        };
      }
      break;
    default:
      return state;
  }
  return {
    ...state,
    ...{
      [chartID]: chartData,
    },
  };
};

export const customChartData = (state = {}, action) => {
  if (!isCustomChartMetricsAction(action.type)) {
    return state;
  }
  const chartID = action.chartID;
  let chartData = {
    loading: false,
    data: [],
    query: {},
  };
  switch (action.type) {
    case Constants.METRICS_CUSTOM_CHART_DATA_REQUEST:
      chartData = {
        loading: true,
        data: [],
        query: {},
      };
      break;
    case Constants.METRICS_CUSTOM_CHART_DATA_SUCCESS:
      chartData = {
        loading: false,
        timeStamp: new Date().getTime(),
        data: (action.payload && action.payload.data) || [],
        query: (action.payload && action.payload.query) || {},
      };
      break;
    case Constants.METRICS_CUSTOM_CHART_DATA_ERROR:
      break;
    default:
      return state;
  }
  return {
    ...state,
    ...{
      [chartID]: chartData,
    },
  };
};

const apiMap = (state = {}, action) => {
  switch (action.type) {
    case Constants.METRICS_API_LIST_REQUEST:
      return state;
    case Constants.METRICS_API_LIST_ERROR:
      return state;
    case Constants.METRICS_API_LIST_SUCCESS: {
      const newState = parseEntityListResponse(action);
      return newState;
    }
    default:
      return state;
  }
};

const apiPerOrgList = (state = [], action) => {
  switch (action.type) {
    case Constants.METRICS_API_PER_ORG_LIST_REQUEST:
      return state;
    case Constants.METRICS_API_PER_ORG_LIST_ERROR:
      return state;
    case Constants.METRICS_API_PER_ORG_LIST_SUCCESS: {
      const newState = action.payload.results;
      return newState;
    }
    default:
      return state;
  }
};

const apisWithRLQList = (state = {}, action) => {
  switch (action.type) {
    case Constants.METRICS_APIS_WITH_RLQ_LIST_REQUEST:
      return state;
    case Constants.METRICS_APIS_WITH_RLQ_LIST_ERROR:
      return state;
    case Constants.METRICS_APIS_WITH_RLQ_LIST_SUCCESS: {
      const itemsMap = {};
      const apis = get(action, 'payload.results', []);
      forEach(apis, api => {
        itemsMap[api.apiUuid] = api.apiName;
      });
      return itemsMap;
    }
    default:
      return state;
  }
};

const apiKeyMap = (state = {}, action) => {
  switch (action.type) {
    case Constants.METRICS_API_KEY_LIST_REQUEST:
      return state;
    case Constants.METRICS_API_KEY_LIST_ERROR:
      return state;
    case Constants.METRICS_API_KEY_LIST_SUCCESS: {
      const newState = parseApiKeyListResponse(action);
      return newState;
    }
    default:
      return state;
  }
};

const apiKeyDetailsMap = (state = {}, action) => {
  switch (action.type) {
    case Constants.METRICS_API_KEY_LIST_REQUEST:
      return state;
    case Constants.METRICS_API_KEY_LIST_ERROR:
      return state;
    case Constants.METRICS_API_KEY_LIST_SUCCESS: {
      const newState = getApiKeyDetailsFromResponse(action);
      return newState;
    }
    default:
      return state;
  }
};

const appMap = (state = {}, action) => {
  switch (action.type) {
    case Constants.METRICS_APP_LIST_REQUEST:
      return state;
    case Constants.METRICS_APP_LIST_ERROR:
      return state;
    case Constants.METRICS_APP_LIST_SUCCESS: {
      const newState = parseEntityListResponse(action);
      return newState;
    }
    default:
      return state;
  }
};

const appMapForOrgInContext = (state = {}, action) => {
  switch (action.type) {
    case Constants.METRICS_APP_LIST_REQUEST:
      return state;
    case Constants.METRICS_APP_LIST_ERROR:
      return state;
    case Constants.METRICS_APP_LIST_SUCCESS: {
      const newState = parseAPIListResponse(action);
      return newState;
    }
    default:
      return state;
  }
};

const orgMap = (state = {}, action) => {
  switch (action.type) {
    case Constants.METRICS_ORG_LIST_REQUEST:
      return state;
    case Constants.METRICS_ORG_LIST_ERROR:
      return state;
    case Constants.METRICS_ORG_LIST_SUCCESS: {
      const newState = parseEntityListResponse(action);
      return newState;
    }
    default:
      return state;
  }
};

const gatewayMap = (state = {}, action) => {
  switch (action.type) {
    case Constants.METRICS_GATEWAY_LIST_SUCCESS: {
      const newState = parseGatewayListResponse(action);
      return newState;
    }
    case Constants.METRICS_GATEWAY_LIST_REQUEST:
    case Constants.METRICS_GATEWAY_LIST_ERROR:
    default:
      return state;
  }
};
const chartParams = (state = {}, action) => {
  switch (action.type) {
    case Constants.SET_CHART_STATE:
      return {
        ...state,
        [action.chartId]: action.params,
      };
    default:
      return state;
  }
};

const customReports = (state = {}, action) => {
  let newState = {};
  switch (action.type) {
    case Constants.METRICS_CUSTOM_REPORT_REQUEST:
      newState = { loading: true, error: false };
      break;
    case Constants.METRICS_CUSTOM_REPORT_SUCCESS:
      newState = {
        loading: false,
        data: action.payload,
        error: false,
      };
      break;
    case Constants.METRICS_CUSTOM_REPORT_ERROR:
      newState = { loading: false, error: true };
      break;
    default:
      return state;
  }
  return {
    ...state,
    ...newState,
  };
};

const customReportPanels = (state = {}, action) => {
  let newState = {};
  switch (action.type) {
    case Constants.METRICS_CUSTOM_REPORT_GET_PANELS_REQUEST:
      newState = { loading: true, error: false };
      break;
    case Constants.METRICS_CUSTOM_REPORT_GET_PANELS_SUCCESS:
      newState = {
        loading: false,
        data: action.payload,
        error: false,
      };
      break;
    case Constants.METRICS_CUSTOM_REPORT_GET_PANELS_ERROR:
      newState = { loading: false, error: true };
      break;
    default:
      return state;
  }
  return {
    ...state,
    ...newState,
  };
};

export default combineReducers({
  chartParams,
  data,
  apiMap,
  apiKeyMap,
  apiKeyDetailsMap,
  apiPerOrgList,
  apisWithRLQList,
  appMap,
  appMapForOrgInContext,
  orgMap,
  gatewayMap,
  customReports,
  customReportPanels,
  customChartData,
});

export const getChartData =
(state, chartID, metricType = 'default') =>
  (state.metrics &&
  state.metrics.data &&
  state.metrics.data[chartID] &&
  state.metrics.data[chartID][metricType]);

export const getIsChartLoading =
  (state, chartID) => !!(get(state, `metrics.data[${chartID}].default.loadding`));

export const getIsMetricTypeDataLoading =
  (state, chartID, metricType = 'default') =>
  (state.metrics &&
  state.metrics.data &&
  state.metrics.data[chartID] &&
  state.metrics.data[chartID][metricType] &&
  state.metrics.data[chartID][metricType].loading);
export const getApiMap =
  (state) => state.metrics && state.metrics.apiMap;
export const getApiPerOrgList =
  (state) => state.metrics && state.metrics.apiPerOrgList;
export const getApisWithRLQList =
  (state) => state.metrics && state.metrics.apisWithRLQList;
export const getApiKeyMap =
  (state) => state.metrics && state.metrics.apiKeyMap;
export const getAppMap = (state) => {
  if (lowerCase(get(state, 'portalConfig.featureFlags.featureFlagOrgFilterForOrgPublishers.value', 'false')) === 'true') {
    return state.metrics && state.metrics.appMap;
  }
  return state.metrics && state.metrics.appMapForOrgInContext;
};
export const getApiKeyDetailsMap =
  (state) => state.metrics && state.metrics.apiKeyDetailsMap;
export const getOrgMap =
  (state) => state.metrics && state.metrics.orgMap;
export const getGatewayMap =
  (state) => state.metrics && state.metrics.gatewayMap;
export const getChartParams = (state, chartId) =>
    state.metrics && state.metrics.chartParams && state.metrics.chartParams[chartId];

export const getCustomChartData = (state, chartID) =>
  (state.metrics &&
  state.metrics.data &&
  state.metrics.customChartData &&
  state.metrics.customChartData[chartID]);

export const getCustomReports = (state) =>
  state.metrics && state.metrics.customReports;

export const getCustomReportPanels = (state) =>
  state.metrics && state.metrics.customReportPanels;
